/**
 * `useMaintainerMode` — renderer-side mirror of `main/maintainer-mode.ts`'s
 * cached env-flag.
 *
 * The flag is **read once at process start** by the main side
 * (`cacheMaintainerMode`) and frozen for the lifetime of the process.
 * `app:is-maintainer` is the IPC handler that returns the cached boolean.
 * We mirror the same `useFeatureFlags` pattern: long-lived React Query
 * cache, no retries, `staleTime: Infinity`. The value can never change
 * mid-session, so a refetch is pure waste.
 *
 * VM Debug pane consumers MUST NOT call this hook from inside
 * `<VmDebugPane>` itself — the parent `Workspace.tsx` reads it once
 * and threads the result through as a `maintainerMode` prop. Pulling
 * the hook into the pane component would force every SSR test
 * rendering `Workspace` to wrap in a `QueryClientProvider`. A-1's
 * `<VmStatusPill>` first iteration hit exactly that trap; the fix
 * (passing `cloudVmEnabled` as a prop from the parent) is the
 * pattern this hook mirrors.
 *
 * The returned `loaded` flag distinguishes the in-flight window from
 * the resolved "ON / OFF" answer. Most callers should gate UI surfaces
 * on `loaded && maintainer`, NOT just `maintainer`, so the maintainer-
 * only surface never flashes before the IPC resolves.
 */

import { useQuery } from '@tanstack/react-query';

const MAINTAINER_QUERY_OPTIONS = {
  // `cacheMaintainerMode()` runs once at app start; the value cannot
  // change mid-process. A refetch would never see a different answer.
  staleTime: Infinity,
  // No retries — the IPC handler is registered at app start and is
  // there for the entire process lifetime. A single failure is
  // structural; surface it rather than mask with retries.
  retry: false,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

export interface MaintainerModeState {
  /** True once the `app:is-maintainer` IPC has resolved. False during
   *  the in-flight window before the first response. */
  loaded: boolean;
  /** True iff `CRUCIBLE_MAINTAINER_MODE=1` was set when Foundry
   *  launched. Read-once, frozen at process start. */
  maintainer: boolean;
}

export function useMaintainerMode(): MaintainerModeState {
  const query = useQuery<boolean>({
    queryKey: ['app:is-maintainer'],
    queryFn: async () => {
      // SSR / test environments may not stub the IPC bridge; fall back
      // to OFF rather than throwing inside React Query (which would
      // otherwise leak as an unhandled rejection).
      if (typeof window === 'undefined' || !window.crucible) {
        return false;
      }
      return await window.crucible.invoke('app:is-maintainer');
    },
    ...MAINTAINER_QUERY_OPTIONS,
  });

  return {
    loaded: query.isSuccess,
    maintainer: query.data ?? false,
  };
}
